import { Home, Info } from "lucide-react";
import {
    Sidebar,
    SidebarContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    SidebarGroup,
    SidebarFooter,
    SidebarMenuButton
} from "@/components/ui/sidebar";
import { IdentitySwitcher } from "./IdentitySwitcher";
import { KaizenLogo } from "./KaizenLogo";
import { Separator } from "@/components/ui/separator";
import { LoadingBoundary, LoadingFallbackProvider } from "@/ui/LoadingBoundary";
import { LazyAssessmentSidebarNav } from "@/assessment/lazyAssessmentComponents";
import { TransitionSidebarMenuButton } from "./SidebarButtons";
import { Link, useLocation } from "react-router-dom";
import { useParallel } from "@/api/useParallel";
import { reexported__useCurrentUser } from "@/user/api/userGetters";
import { reexported__useCurrentFirm } from "@/firm/api/firmGetters";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";

export function AppSidebar() {
    return (
        <Sidebar className="absolute w-full border-transparent">
            {/* loading boundary for the sidebar - mainly here for mobile mode, so that opening the sidebar doesn't trigger the top level app spinner */}
            <LoadingBoundary absolute={true}>
                <SidebarHeader className="bg-background">
                    <KaizenLogo className="m-2 mb-0 max-w-24" />
                </SidebarHeader>
                <ContentWithAutoScroll>
                    <SidebarGroup>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <TransitionSidebarMenuButton to="/" icon={Home}>
                                    Home
                                </TransitionSidebarMenuButton>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <Link
                                        to="https://community.kaizenreporting.com/market-abuse-risk-assessment-ujqi8doh"
                                        target="_blank"
                                    >
                                        <Info className="mr-2 h-4 w-4" />
                                        Product Info
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroup>
                    <SidebarGroup>
                        {/* loading boundary for the assessment sidebar nav */}
                        <LoadingBoundary>
                            <LazyAssessmentSidebarNav />
                        </LoadingBoundary>
                    </SidebarGroup>
                </ContentWithAutoScroll>
                <SidebarFooter className="bg-background pb-3">
                    <Separator />
                    <IdentitySwitcher />
                </SidebarFooter>
            </LoadingBoundary>
            {/* enforce data loading before sidebar is shown for initial app load - react seems more likely to also load data within loading boundary (above) if preloading happens in a sub component (EnforceData) */}
            <EnforceData />
        </Sidebar>
    );
}

const ContentWithAutoScroll = ({ children }: { children: ReactNode }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { pathname } = useLocation();
    const lastScrolledToRef = useRef<Element>();

    // force a re-render of sidebar content when the loading fallback is unmounted
    const [forceTrigger, setForceTrigger] = useState(0);
    const onFallbackCleanup = () => {
        setForceTrigger(x => x + 1);
    };

    useLayoutEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        forceTrigger; // ensure forceTrigger is in dependency array

        if (containerRef.current) {
            const active = containerRef.current.querySelector(
                "[data-active='true']"
            );
            if (active) {
                if (active !== lastScrolledToRef.current) {
                    active.scrollIntoView({
                        block: lastScrolledToRef.current ? "nearest" : "center", // cenetered on first load, nearest when navigating
                        behavior: lastScrolledToRef.current
                            ? "smooth"
                            : "instant" // instant scroll on first load, smooth scroll on subsequent loads
                    });
                    lastScrolledToRef.current = active;
                }
            }
        }
    }, [forceTrigger, pathname]);

    return (
        <LoadingFallbackProvider onFallbackCleanup={onFallbackCleanup}>
            <SidebarContent className="bg-background gap-0" ref={containerRef}>
                {children}
            </SidebarContent>
        </LoadingFallbackProvider>
    );
};

const EnforceData = () => {
    // ensure that the user and firm are loaded before the sidebar is shown
    // this should mean that the sidebar loading boundary isn't triggered on initial page load because data for IdentitySwitcher is already available
    useParallel(
        () => reexported__useCurrentUser(),
        () => reexported__useCurrentFirm()
    );
    return null;
};
